/**
 * LessonWorkspace.tsx
 *
 * The permanent lesson-centered hub. One lesson. Four renders.
 *
 * The teacher fills out one form, once. Everything else — Activity Sheet,
 * Presentation, Assessment — is generated or re-rendered from the stored
 * lesson blueprint without ever re-entering the prompt.
 */

import React from 'react';
import { motion } from 'framer-motion';
import {
  BookOpen, FileText, Monitor, ClipboardList,
  ChevronLeft, CheckCircle2, Circle, Play, Eye,
  RefreshCw, Calendar, GraduationCap, Target,
} from 'lucide-react';
import { useResources, type SavedResource } from './resources-context';

// ── Types ─────────────────────────────────────────────────────────────────────

interface LessonWorkspaceProps {
  lessonResource: SavedResource;
  onBack: () => void;
  /** Navigate to a generator, with fromWorkspace=true so Back returns here. */
  onNavigateTo: (section: string, ctx?: {
    fromLessonTitle?: string;
    fromLessonId?: string;
    fromWorkspace?: boolean;
  }) => void;
  /** Open an existing companion resource in its generator. */
  onOpenResource: (resource: SavedResource) => void;
}

interface OutputRow {
  type: 'activity' | 'presentation' | 'assessment';
  label: string;
  navKey: string;
  icon: React.ElementType;
  color: string;
  bg: string;
  ring: string;
  border: string;
}

const OUTPUTS: OutputRow[] = [
  {
    type: 'activity',
    label: 'Activity Sheet',
    navKey: 'activity-sheets',
    icon: FileText,
    color: 'text-violet-600',
    bg: 'bg-violet-50',
    ring: 'ring-violet-200',
    border: 'border-violet-100',
  },
  {
    type: 'presentation',
    label: 'Presentation',
    navKey: 'presentations',
    icon: Monitor,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    ring: 'ring-amber-200',
    border: 'border-amber-100',
  },
  {
    type: 'assessment',
    label: 'Assessment',
    navKey: 'assessments',
    icon: ClipboardList,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    ring: 'ring-emerald-200',
    border: 'border-emerald-100',
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

export function LessonWorkspace({
  lessonResource,
  onBack,
  onNavigateTo,
  onOpenResource,
}: LessonWorkspaceProps) {
  const { resources } = useResources();

  // Find companion resources linked to this lesson
  const companions = resources.filter(
    r => r.lessonId === lessonResource.id && !r.isArchived,
  );

  function getLatestCompanion(type: 'activity' | 'presentation' | 'assessment') {
    return companions
      .filter(r => r.type === type)
      .sort((a, b) => new Date(b.lastEditedAt).getTime() - new Date(a.lastEditedAt).getTime())[0];
  }

  function renderGenerator(navKey: string) {
    onNavigateTo(navKey, {
      fromLessonTitle: lessonResource.title,
      fromLessonId: lessonResource.id,
      fromWorkspace: true,
    });
  }

  const dateStr = new Date(lessonResource.savedAt).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });

  return (
    <div className="max-w-2xl mx-auto py-8 px-4 print:hidden">

      {/* ── Back ────────────────────────────────────────────────────────── */}
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-6 transition-colors"
      >
        <ChevronLeft size={16} />
        My Lessons
      </button>

      {/* ── Lesson header ───────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mb-8"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <BookOpen size={22} className="text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 leading-tight">
              {lessonResource.title}
            </h1>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-sm text-slate-500">
              {lessonResource.grade && (
                <span className="flex items-center gap-1">
                  <GraduationCap size={13} />Grade {lessonResource.grade}
                </span>
              )}
              {lessonResource.subject && (
                <span className="flex items-center gap-1">
                  <Target size={13} />{lessonResource.subject}
                </span>
              )}
              {lessonResource.standard && (
                <span className="font-mono text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                  {lessonResource.standard}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Calendar size={13} />{dateStr}
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* ── Lesson Plan row ─────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28, delay: 0.04 }}
        className="bg-white rounded-2xl border border-slate-200 shadow-sm mb-3 p-5
                   flex items-center gap-4"
      >
        <div className="w-10 h-10 rounded-xl bg-blue-50 ring-1 ring-blue-200
                        flex items-center justify-center flex-shrink-0">
          <BookOpen size={18} className="text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-800 text-sm">Lesson Plan</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <CheckCircle2 size={13} className="text-emerald-500" />
            <span className="text-xs text-emerald-600 font-medium">Generated</span>
          </div>
        </div>
        <button
          onClick={() => onNavigateTo('lesson-planner', {
            fromLessonTitle: lessonResource.title,
            fromLessonId: lessonResource.id,
            fromWorkspace: true,
          })}
          className="flex items-center gap-1.5 text-sm font-medium text-blue-600
                     hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-xl
                     px-3 py-1.5 transition-colors"
        >
          <Eye size={14} />View / Edit
        </button>
      </motion.div>

      {/* ── Section label ───────────────────────────────────────────────── */}
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-1 mb-3 mt-6">
        Instructional Resources
      </p>

      {/* ── Companion rows ──────────────────────────────────────────────── */}
      {OUTPUTS.map((output, i) => {
        const companion = getLatestCompanion(output.type);
        const Icon = output.icon;

        return (
          <motion.div
            key={output.type}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.28, delay: 0.08 + i * 0.06 }}
            className="bg-white rounded-2xl border border-slate-200 shadow-sm mb-3
                       p-5 flex items-center gap-4"
          >
            <div className={`w-10 h-10 rounded-xl ${output.bg} ring-1 ${output.ring}
                             flex items-center justify-center flex-shrink-0`}>
              <Icon size={18} className={output.color} />
            </div>

            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-800 text-sm">{output.label}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                {companion ? (
                  <>
                    <CheckCircle2 size={13} className="text-emerald-500" />
                    <span className="text-xs text-emerald-600 font-medium">Generated</span>
                  </>
                ) : (
                  <>
                    <Circle size={13} className="text-slate-300" />
                    <span className="text-xs text-slate-400">Not yet generated</span>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {companion ? (
                <>
                  <button
                    onClick={() => onOpenResource(companion)}
                    className={`flex items-center gap-1.5 text-sm font-medium
                                ${output.color} ${output.bg} hover:opacity-90
                                rounded-xl px-3 py-1.5 transition-all`}
                  >
                    <Eye size={14} />View
                  </button>
                  <button
                    onClick={() => renderGenerator(output.navKey)}
                    className="flex items-center gap-1.5 text-sm text-slate-500
                               hover:text-slate-700 bg-slate-50 hover:bg-slate-100
                               rounded-xl px-3 py-1.5 transition-colors"
                    title={`Re-render ${output.label} from lesson blueprint`}
                  >
                    <RefreshCw size={14} />Re-render
                  </button>
                </>
              ) : (
                <button
                  onClick={() => renderGenerator(output.navKey)}
                  className={`flex items-center gap-1.5 text-sm font-semibold
                              ${output.color} ${output.bg} ring-1 ${output.ring}
                              rounded-xl px-4 py-2 transition-all shadow-sm
                              hover:opacity-90`}
                >
                  <Play size={14} />Render {output.label}
                </button>
              )}
            </div>
          </motion.div>
        );
      })}

      {/* ── Footer note ─────────────────────────────────────────────────── */}
      <p className="text-xs text-center text-slate-400 mt-6 px-4">
        All resources render from the same lesson blueprint.
        The teacher fills out one form, ever.
      </p>
    </div>
  );
}
